import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageSquare, MapPin, Clock, BadgeCheck, Star } from 'lucide-react';
const stats = [{
  value: 'Same Day',
  label: 'Availability'
}, {
  value: '50 Miles',
  label: 'Service Radius'
}, {
  value: 'Free',
  label: 'Estimates'
}, {
  value: 'One Call',
  label: 'Everything Gone'
}];
export function Hero() {
  return <section className="relative min-h-[92vh] flex items-center pt-24 lg:pt-28 overflow-hidden bg-[#0E0E0E]" data-id="element-336" data-mp="1297cu5rar3ad">
      {/* Background photo */}
      <div className="absolute inset-0 z-0" data-id="element-337" data-mp="xbpbp1jitscx">
        <img src="https://cdn.magicpatterns.com/uploads/4hKbcVapnEVcG7A8fQxJcc/josejunk1.png" alt="Jose's Junk Removal dump truck at golden hour in a Monterey neighborhood" className="w-full h-full object-cover object-center" data-id="element-338" data-mp="sbb2vha3iedu" />
        {/* Layered scrims: dark on the left for copy, warm sunset preserved on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E0E0E] via-[#0E0E0E]/85 to-transparent" data-id="element-339" data-mp="380vz9yqbyvdo" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E] via-transparent to-[#0E0E0E]/60" data-id="element-340" data-mp="3j9ahhe4fas5o" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-14 lg:py-20" data-id="element-341" data-mp="3o59bpicfn4e0">
        <div className="max-w-3xl" data-id="element-342" data-mp="39cpawocr98zq">
          {/* Eyebrow */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex flex-wrap items-center gap-2 mb-7" data-id="element-343" data-mp="3e84rvp627l0d">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-md" data-id="element-344" data-mp="2qk3muq83ampp">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8102E]" data-id="element-345" data-mp="2vjq0h0rty4z1" />
              <span className="text-white text-[11px] font-bold tracking-[0.2em] uppercase" data-id="element-346" data-mp="2hincujl6smm0">
                Monterey's #1 Full-Service Crew
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-md" data-id="element-347" data-mp="s21k8hdb6o2m">
              {Array.from({
              length: 5
            }).map((_, i) => <Star key={i} size={12} className="fill-[#FBBC04] text-[#FBBC04]" data-id="element-348" data-mp="ibyrmia3nc4t" data-mpi={i} />)}
              <span className="text-white text-[11px] font-bold tracking-wide ml-1" data-id="element-349" data-mp="db2ct9o7m5km">
                5.0 on Google
              </span>
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.05,
          ease: [0.23, 1, 0.32, 1]
        }} className="text-[3.25rem] sm:text-7xl lg:text-[5.5rem] font-bold text-white leading-[0.95] mb-7" data-id="element-350" data-mp="15zq0td0cf91v">
            ONE CALL.
            <br data-id="element-351" data-mp="3hqu2kaecg816" />
            <span className="text-[#C8102E]" data-id="element-352" data-mp="3hqaj2p91pbl1">ONE TEAM.</span>
            <br data-id="element-353" data-mp="1x9b4uzsqezc1" />
            EVERYTHING GONE.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.12,
          ease: [0.23, 1, 0.32, 1]
        }} className="text-lg sm:text-xl text-gray-300 mb-9 max-w-2xl leading-relaxed" data-id="element-354" data-mp="1xb5qkgbtuvh6">
            Junk removal, demolition, and full property cleanouts for Monterey
            homeowners, landlords, and contractors — without renting a truck,
            hiring sketchy help, or wasting another weekend.
          </motion.p>

          {/* CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex flex-col sm:flex-row gap-3 mb-8" data-id="element-355" data-mp="2ozz5maqhu2p6">
            <a href="tel:8312407401" className="group flex items-center justify-center gap-3 bg-[#C8102E] text-white px-8 py-5 rounded-xl font-bold text-lg hover:bg-[#8B0A20] transition-colors shadow-[0_10px_40px_rgba(200,16,46,0.45)]" data-id="element-356" data-mp="uou359r57wbf">
              <Phone size={22} data-id="element-357" data-mp="3vhfe464r7zip" />
              <span data-id="element-358" data-mp="e9jwdqsj53qb">Call (831) 240-7401</span>
            </a>
            <a href="sms:8312407401" className="flex items-center justify-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md px-8 py-5 rounded-xl font-bold text-lg transition-colors" data-id="element-359" data-mp="e91wmk6ns7dn">
              <MessageSquare size={22} data-id="element-360" data-mp="3crz2amnmckmz" />
              <span data-id="element-361" data-mp="3cp30uockbdts">Text a Photo</span>
            </a>
          </motion.div>

          {/* Trust line */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.26
        }} className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400 font-medium mb-12 lg:mb-0" data-id="element-362" data-mp="3ri6kr2dmm3no">
            <span className="flex items-center gap-2" data-id="element-363" data-mp="2tzwrhrxnrykn">
              <BadgeCheck size={16} className="text-[#C8102E]" data-id="element-364" data-mp="19ixc3z5lzga2" />
              Free, no-obligation estimates
            </span>
            <span className="flex items-center gap-2" data-id="element-365" data-mp="1eafcom3pzeui">
              <MapPin size={16} className="text-[#C8102E]" data-id="element-366" data-mp="1eb6u8t0hfc1s" />
              Monterey + 50 miles
            </span>
            <span className="flex items-center gap-2" data-id="element-367" data-mp="14jaxpme1ntzo">
              <Clock size={16} className="text-[#C8102E]" data-id="element-368" data-mp="14k3ze8y5btay" />
              Same-day &amp; next-day
            </span>
          </motion.div>
        </div>
      </div>

      {/* Stat strip pinned to the bottom of the hero */}
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 0.34,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-black/40 backdrop-blur-md" data-id="element-369" data-mp="1x5vpgnyfm83r">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-370" data-mp="7vebo1zu86h2">
          <dl className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10" data-id="element-371" data-mp="bvoifgvgf0wz">
            {stats.map(stat => <div key={stat.label} className="py-5 px-4 text-center lg:text-left" data-id="element-372" data-mp="2egsn698777q2">
                <dt className="sr-only" data-id="element-373" data-mp="29izdv7bllxlk">{stat.label}</dt>
                <dd data-id="element-374" data-mp="24r8zmq9jipov">
                  <span className="block font-oswald text-xl sm:text-2xl font-bold text-white uppercase leading-none" data-id="element-375" data-mp="24qpihbrps5bw">
                    {stat.value}
                  </span>
                  <span className="block text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-gray-400 font-bold mt-1.5" data-id="element-376" data-mp="1vrlxu3cwfhva">
                    {stat.label}
                  </span>
                </dd>
              </div>)}
          </dl>
        </div>
      </motion.div>
    </section>;
}